import {css, StyleSheet} from 'aphrodite';
import {getGLContext} from 'molstar/lib/commonjs/mol-gl/webgl/context';
import {BuiltInTrajectoryFormat} from 'molstar/lib/commonjs/mol-plugin-state/formats/trajectory';
import {PluginUIContext} from 'molstar/lib/commonjs/mol-plugin-ui/context';
import {Plugin} from 'molstar/lib/commonjs/mol-plugin-ui/plugin';
import {setDebugMode, setProductionMode} from 'molstar/lib/commonjs/mol-util/debug';
import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
// @ts-expect-error no types
import 'molstar/lib/mol-plugin-ui/skin/light.scss';

import {assert} from 'Asserts';
import makeCancelable from 'lib/makeCancelable';
import {
  downloadRemoteStructure,
  getPluginState,
  selectStructureResidues,
  ViewerAutoPreset,
  SurfacePreset,
  getChainSequences,
  findChainsBySequence,
} from 'molbio/molstar/util';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as logging from 'util/Logging';

setDebugMode(window.debug);
setProductionMode(!window.debug);

export interface MolstarWrapperProps {
  format: BuiltInTrajectoryFormat;
  url: string;
  className?: string;
  interactionAnalyticsCallback?: () => void;
  residueStart?: number;
  residueEnd?: number | null;
  disableSelection?: boolean;
  surfaceView?: boolean;
  currentProteinSequence?: string;
}

function isWebGLAvailable(): boolean {
  const canvas = document.createElement('canvas');
  const gl = getGLContext(canvas);
  return gl != null;
}

/**
 * Performs the initialization of the Mol* plugin and renders the inner viewer component once initialized,
 * showing a loading throbber while waiting. It's important that the plugin is initialized before it is
 * used for rendering or operations are performed on it, so it's useful to separate out the inner viewer
 * rendering and logic from this setup.
 *
 * **Don't import this directly! import from `ViewerWrapper` instead for async loading.**
 */
export default function MolstarViewerWrapper({
  format,
  url,
  className,
  interactionAnalyticsCallback,
  residueStart,
  residueEnd,
  disableSelection,
  surfaceView = false,
  currentProteinSequence,
}: MolstarWrapperProps): JSX.Element {
  const [pluginContextInitialized, setPluginContextInitialized] = useState(false);
  const [hasValidDimensions, setHasValidDimensions] = useState(true);
  const webGLAvailable = useMemo(() => {
    const available = isWebGLAvailable();
    if (!available) {
      logging.warn('WebGL is not available');
    }
    return available;
  }, []);
  const pluginContext = useRef<PluginUIContext>(
    new PluginUIContext(getPluginState(surfaceView ? SurfacePreset.id : undefined))
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!webGLAvailable) {
      // Don't attempt to initialize if WebGL is not available
      return;
    }

    const pluginContextReference = pluginContext.current;

    const {promise, cancel} = makeCancelable(pluginContextReference.init());

    async function loadPluginContext(): Promise<void> {
      try {
        await promise;
        // Register both presets so they're available in the UI
        pluginContextReference.builders.structure.representation.registerPreset(ViewerAutoPreset);
        pluginContextReference.builders.structure.representation.registerPreset(SurfacePreset);
        setPluginContextInitialized(true);
      } catch (e) {
        if (!e.isCanceled) {
          throw e;
        }
      }
    }

    loadPluginContext();

    return () => {
      cancel();
      pluginContextReference.dispose();
    };
  }, [webGLAvailable]);

  // Monitor container dimensions to prevent WebGL errors with 0x0 textures
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const checkDimensions = (): void => {
      const rect = container.getBoundingClientRect();
      const valid = rect.width > 0 && rect.height > 0;

      if (valid !== hasValidDimensions) {
        setHasValidDimensions(valid);

        // Pause/resume molstar rendering based on dimensions
        const canvas3d = pluginContext.current.canvas3d;
        if (canvas3d) {
          if (!valid) {
            // Pause rendering immediately when dimensions become invalid
            canvas3d.pause(true);
          } else if (pluginContextInitialized) {
            // Resume when dimensions become valid again (only if initialized)
            canvas3d.animate();
          }
        }
      }
    };

    // Check initial dimensions
    checkDimensions();

    // Use ResizeObserver to track dimension changes
    const resizeObserver = new ResizeObserver(checkDimensions);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [hasValidDimensions, pluginContextInitialized]);

  if (!webGLAvailable) {
    return (
      <div ref={containerRef} className={css(styles.webGLUnavailableMessage)}>
        <div>
          <p>Unable to initialize 3D viewer.</p>
          <p>
            WebGL is not available in your browser. Try enabling WebGL in your browser settings by following
            {` `}
            <a href="https://get.webgl.org/" target="_blank" rel="noopener noreferrer">
              the guide here
            </a>
            .
          </p>
          <p>If this issue persists, please contact your IT administrator or support@benchling.com.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      onPointerEnter={pluginContextInitialized ? interactionAnalyticsCallback : undefined}
    >
      {pluginContextInitialized && hasValidDimensions ? (
        <MolstarViewer
          pluginContext={pluginContext.current}
          format={format}
          url={url}
          residueStart={residueStart}
          residueEnd={residueEnd}
          key={`${url}-${format}`}
          disableSelection={disableSelection}
          surfaceView={surfaceView}
          currentProteinSequence={currentProteinSequence}
        />
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </div>
  );
}

function MolstarViewer({
  pluginContext,
  format,
  url,
  residueStart,
  residueEnd,
  disableSelection,
  surfaceView = false,
  currentProteinSequence,
}: {
  pluginContext: PluginUIContext;
  format: BuiltInTrajectoryFormat;
  url: string;
  residueStart?: number;
  residueEnd?: number | null;
  disableSelection?: boolean;
  surfaceView?: boolean;
  currentProteinSequence?: string;
}): JSX.Element {
  const [structureLoaded, setStructureLoaded] = useState(false);
  const [chainIds, setChainIds] = useState<Array<string> | undefined>();

  useEffect(() => {
    setStructureLoaded(false);
    const {promise, cancel} = makeCancelable(downloadRemoteStructure(pluginContext, url, format));

    async function loadStructure(): Promise<void> {
      try {
        await promise;
        setStructureLoaded(true);
      } catch (e) {
        if (!e.isCanceled) {
          throw e;
        }
      }
    }

    loadStructure();

    return () => {
      cancel();
    };
  }, [pluginContext, format, url]);

  // Extract chains and match sequence after structure loads
  useEffect(() => {
    if (!structureLoaded || !currentProteinSequence) {
      return;
    }

    const structures = pluginContext.managers.structure.component.currentStructures;
    if (structures.length !== 1 || !structures[0].properties?.cell.obj?.data) {
      return;
    }

    const structure = structures[0].properties.cell.obj.data;

    try {
      const chains = getChainSequences(structure);
      const matchedChainIds = findChainsBySequence(chains, currentProteinSequence);

      if (matchedChainIds.length > 0) {
        setChainIds(matchedChainIds);
      } else {
        setChainIds(undefined);
      }
    } catch (error) {
      logging.error('Error matching protein sequence to chains:', error);
      setChainIds(undefined);
      throw error;
    }
  }, [structureLoaded, currentProteinSequence, pluginContext]);

  useEffect(() => {
    if (disableSelection) {
      pluginContext.managers.interactivity.lociSelects.deselectAll();
      return;
    }
    if (
      residueStart != null &&
      structureLoaded &&
      pluginContext.managers.structure.component.currentStructures.length === 1 &&
      pluginContext.managers.structure.component.currentStructures[0].properties?.cell.obj?.data
    ) {
      if (residueEnd == null) {
        pluginContext.managers.interactivity.lociSelects.deselectAll();
      } else {
        assert(
          pluginContext.managers.structure.component.currentStructures.length <= 1,
          'Expected there to be one structure loaded into Mol*'
        );
        selectStructureResidues(
          pluginContext,
          pluginContext.managers.structure.component.currentStructures[0].properties.cell.obj.data,
          residueStart,
          residueEnd,
          chainIds
        );
      }
    }
  }, [pluginContext, residueStart, residueEnd, structureLoaded, disableSelection, chainIds]);

  return <Plugin plugin={pluginContext} />;
}

const styles = StyleSheet.create({
  webGLUnavailableMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    textAlign: 'center',
    color: GRAY_DARKER,
    minHeight: '200px',
    ...FONT_PROXIMA_13,
  },
});
